import { gsap, Power3 } from "gsap";
import { Point, Text } from "pixi.js";

import {SanQuentin2} from "../../SanQuentin2";
import { StageLayer } from '@nolimitcity/slot-launcher/bin/StageLayer';
import { StateHandler } from "../../../core/utils/StateHandler";
import { Sequence } from "../../../core/displayobject/Sequence";
import { IDimOrientation, LayoutDimData, ResizeHelper } from "../../../core/utils/ResizeHelper";
import { MathHelper } from "../../../core/utils/MathHelper";
import { Helper } from "../../../core/utils/Helper";
import { TextStyleConfig } from "../../config/translate/TextStyleConfig";
import { TranslationConfig } from "../../config/translate/TranslationConfig";
import { GameSetting } from "../../../core/setting/GameSetting";

export class XWayStateLevel extends StateHandler{

    public static LEVEL_1:number = 0;
    public static LEVEL_2:number = 1;
    public static LEVEL_3:number = 2;

    protected static ValueLvls:number[] = [800,2200,4600];
    protected static TextureLvls:string[] = ["Header_A","Header_B","Header_C"];
    protected static SpineLvls:string[] = ["Header_Loop_A","Header_Loop_B","Header_Loop_C"];

    public get lastLvl():number{
        return XWayStateLevel.ValueLvls[this.value-1] ? XWayStateLevel.ValueLvls[this.value-1] : 0;
    }

    public get nextLvl():number{
        return XWayStateLevel.ValueLvls[this.value+1] ? XWayStateLevel.ValueLvls[this.value+1] : 100000000000000;
    }

    public get valueLvl():number{
        return XWayStateLevel.ValueLvls[this.value]
    }

    public get TextureLvl():string{
        return XWayStateLevel.TextureLvls[this.value]
    }

    public get SpineLvl():string{
        return XWayStateLevel.SpineLvls[this.value]
    }

    public set(value:number):boolean{
        if(value > XWayStateLevel.LEVEL_3 || value < XWayStateLevel.LEVEL_1) return false;
        super.set(value);
        return true;
    }
}

export class XWaysCounter extends PIXI.Container implements StageLayer {

    protected _maxWinPost:Text;
    protected _maxWinAmount:Text;

    protected value:number = 1024;

    protected _textCounter:Text;
    protected _textPost:Text;
    private xway_logo:Sequence;

    protected _timeLine:gsap.core.Timeline;
    protected _xwayState:XWayStateLevel = new XWayStateLevel(XWayStateLevel.LEVEL_1);
    //Header
    protected _countsUp:boolean = true;
    protected _isFS:boolean = true;
    protected _showJuri:boolean = true;

    protected should_be_seen:boolean = true;
    protected fontSize:number;

    public get isHiding():boolean{
        return this.alpha == 0 || !this.visible;
    }

    protected readonly CounterDim:IDimOrientation = {
        landscape :{
            scale:      new Point( 0.8, 0.8),
            position:   new Point( 730, 236),
        },
        portrait :{
            scale:      new Point( 0.8, 0.8),
            position:   new Point( 650, -145)
        }
    };

    // text style for 39647 Ways
    constructor() {
        super();
        this.initAnimations();
    }

    public get dim():LayoutDimData{ return ResizeHelper.calcDim(this.CounterDim); };

    protected initAnimations():void{

        const pathTexture:string = "Xways_Sign/" + (ResizeHelper.isLandscape ? "Xways_Left" : "Xways_Top")
        this.xway_logo = this.addChild(new Sequence(SanQuentin2.assetLoader.getTextures(pathTexture)));
        this.xway_logo.anchor.set(0.5);
        this.xway_logo.alpha = 1;
        
        this._textCounter = this.createTextObject();
        this._textPost = this.createTextObject();
        this._textPost.position.y += this._textCounter.height/1.5;
        this._textPost.text = GameSetting.translate(TranslationConfig.SIDE_COUNTER_WAYS);

        this._maxWinPost = this.createTextObject();
        this._maxWinPost.style = TextStyleConfig.MAX_WIN_TEXT;
        this._maxWinPost.text = GameSetting.translate(TranslationConfig.WIN_UP_TO);

        this._maxWinAmount = this.createTextObject();
        this._maxWinAmount.style = TextStyleConfig.MAX_WIN_TEXT;
        this._maxWinAmount.text = "200 000 X";
        this._maxWinAmount.style.wordWrapWidth = 800;
        this._maxWinAmount.visible = false;
        this._maxWinPost.visible = false;
        
        this._showJuri = GameSetting.showLowProbabilityGfx;
        if(this._showJuri){
            this._maxWinAmount.visible = true;
            this._maxWinPost.visible = true;
        }

        this.fontSize = TextStyleConfig.TOTAL_XWAYS.fontSize as number;
            
        this.updateValue();
        this.onResize();
    }

    protected createTextObject():Text{
        const textObj = new Text(this.value.toString());
        textObj.style = TextStyleConfig.TOTAL_XWAYS;

        textObj.anchor.set(0.5,0);
        this.addChild(textObj);
        return textObj;
    }

    public hardSetValue(value:number):void{
        this.value = value;
        this.updateValue();
    }

    public setValue(from:number | undefined, to:number, duration:number):gsap.core.Timeline {
        this._timeLine = Helper.setTimeline(this._timeLine);
        if(!this._textCounter.visible) return this._timeLine;
        if(this.value == to) return gsap.timeline();
        let fromValue = from != undefined ? from : this.value;
        this._textCounter.style = TextStyleConfig.TOTAL_XWAYS;
        this._countsUp = to > fromValue;

        if(this._countsUp){
            gsap.to(this._textCounter.scale,0.15,{x:1.15,y:1.15}).eventCallback("onComplete",() => {gsap.to(this._textCounter.scale,0.15,{x:1,y:1})})
        }else{
            gsap.to(this._textCounter.scale,0.1,{x:0.95,y:0.95}).eventCallback("onComplete",() => {gsap.to(this._textCounter.scale,0.1,{x:1,y:1})})
        }

        this._textCounter.style.fontSize = this.fontSize;
        
        this._timeLine = new gsap.core.Timeline({ onUpdate:() => { 
            this.updateValue();
        }, 
            onComplete: () => {
                this.updateValue();
                this.onResize();
            }
        });
        this._timeLine.fromTo(this,duration,{value:fromValue},{value:to});
        return this._timeLine;
    }

    public hide():void {
        this.alpha = 0;
        this.should_be_seen = false;
    }

    public counterActive(value:boolean, isFS?:boolean):void {
        this._isFS = !!isFS;
        const toAlpha = ResizeHelper.isIPadd ? 0 : 1;

        this.should_be_seen = value;

        if(this._showJuri){
            this._maxWinPost.visible = this._maxWinAmount.visible = !this._isFS;
        }

        gsap.to(this,{alpha: value ? toAlpha : 0, duration:0.5})
        this.onResize();
    }

    public onResize():void{

        if(this.should_be_seen){
            this.visible = !ResizeHelper.isIPadd;
        }

        this._maxWinPost.style.wordWrapWidth = ResizeHelper.isLandscape ? 160 : 300;

        this.position = this.dim.position;
        if(this._isFS){
            if(ResizeHelper.isLandscape){
                this.position.y = 180;
            }else
                this.position.y += ResizeHelper.isLandscape ? 0 : -15;
        }

        this.scale = this.dim.scale;

        this._maxWinPost.scale.set(1);
        this._textPost.scale.set(1);

        this._textCounter.position.y = ResizeHelper.isLandscape ? -10 : -26;
        this._textPost.position.y = this._textCounter.position.y+this._textCounter.height/1.5;

        const sizingItPos:number = this._textPost.width > 110 ? 110/this._textPost.width : 1;
        this._textPost.scale = MathHelper.pointMul(<PIXI.Point>this._textPost.scale, sizingItPos);

        if(ResizeHelper.isLandscape){
            const sizingIt:number = this._maxWinPost.width > 160 ? 160/this._maxWinPost.width : 1;
            this._maxWinPost.scale = MathHelper.pointMul(<PIXI.Point>this._maxWinPost.scale, sizingIt);
        }else{
            const sizingIt:number = this._maxWinPost.width > 200 ? 200/this._maxWinPost.width : 1;
            this._maxWinPost.scale = MathHelper.pointMul(<PIXI.Point>this._maxWinPost.scale, sizingIt);
        }

        const pathTexture:string = "Xways_Sign/" + (ResizeHelper.isLandscape ? "Xways_Left" : "Xways_Top");
        this.xway_logo.setTexture(SanQuentin2.assetLoader.getTextures(pathTexture));

        this.xway_logo.pivot.x = ResizeHelper.isLandscape ? 0 : 50;
        this.xway_logo.pivot.y =  (ResizeHelper.isLandscape ? 0 : -15);

        this._maxWinPost.position.y = (ResizeHelper.isLandscape ? 104 : 0);
        this._maxWinAmount.position.y = this._maxWinPost.position.y+(this._maxWinPost.height-4)// + this._maxWinAmount.height/2 - 4);

        this._maxWinPost.position.x = ResizeHelper.isLandscape ? 0 : -360;
        this._maxWinAmount.position.x = ResizeHelper.isLandscape ? 0 : -360;

        this._textPost.updateTransform();
        this._maxWinAmount.updateTransform();
    }

    protected updateValue():void{
        this._textCounter.text = this.value.toFixed(0);
        this._textCounter.updateTransform();

        while (this._textCounter.width > 100){
            this._textCounter.style.fontSize = MathHelper.clamp(this._textCounter.style.fontSize - 1, 5, this.fontSize);
        }
        if (this._textCounter.width < 100){
            this._textCounter.style.fontSize = MathHelper.clamp(this._textCounter.style.fontSize + 1, 5, this.fontSize);
        }
    }
}